import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  QrCode, 
  Share2, 
  Download, 
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  ExternalLink,
  AlertTriangle,
  FileCheck,
  Activity,
  Lock,
  Unlock,
  Users,
  Building2,
  Sparkles
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useExchangeAuth } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

// Score circle component
const ScoreCircle = ({ score, size = 'lg' }) => {
  const sizeClasses = {
    sm: 'w-16 h-16 text-xl',
    md: 'w-24 h-24 text-3xl',
    lg: 'w-32 h-32 text-4xl'
  };
  
  const getColor = (s) => {
    if (s >= 80) return { border: 'border-neon-green', text: 'text-neon-green', bg: 'bg-neon-green' };
    if (s >= 60) return { border: 'border-yellow-500', text: 'text-yellow-500', bg: 'bg-yellow-500' };
    if (s >= 40) return { border: 'border-orange-500', text: 'text-orange-500', bg: 'bg-orange-500' };
    return { border: 'border-red-500', text: 'text-red-500', bg: 'bg-red-500' };
  };
  
  const colors = getColor(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className={`${sizeClasses[size]} relative`}>
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-slate-700"
        />
        <motion.circle
          cx="50%"
          cy="50%"
          r="45%"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          className={colors.text}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          style={{ strokeDasharray: circumference }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center font-bold ${colors.text}`}>
        {score}
      </div>
    </div>
  );
};

// Verification badge
const VerificationBadge = ({ verified, label, verifiedAt }) => (
  <div className={`p-3 rounded-lg ${verified ? 'bg-neon-green/10 border border-neon-green/30' : 'bg-slate-700/50 border border-slate-600'}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        {verified ? (
          <CheckCircle2 className="w-5 h-5 text-neon-green" />
        ) : (
          <XCircle className="w-5 h-5 text-slate-500" />
        )}
        <span className={verified ? 'text-white' : 'text-slate-400'}>{label}</span>
      </div>
      {verified && verifiedAt && (
        <span className="text-xs text-slate-400">
          {new Date(verifiedAt).toLocaleDateString()}
        </span>
      )}
    </div>
  </div>
);

// Share modal component
const ShareModal = ({ isOpen, onClose, passportId, verificationUrl }) => {
  const [email, setEmail] = useState('');
  const [expiryHours, setExpiryHours] = useState(168);
  const [sharing, setSharing] = useState(false);
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(verificationUrl);
    toast.success('Verification link copied to clipboard!');
  };
  
  const handleShare = async () => {
    setSharing(true);
    // Simulated share - in production would call API
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Passport shared successfully!');
    setSharing(false);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <Share2 className="w-5 h-5 mr-2 text-neon-cyan" />
          Share Your Passport
        </h3>
        
        <div className="space-y-4">
          {/* Quick copy link */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Verification Link</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={verificationUrl}
                readOnly
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
              />
              <button
                onClick={handleCopyLink}
                className="btn-secondary px-3"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Share with organization */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Share with Organization</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter organization email"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          
          {/* Expiry selection */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Access Expires In</label>
            <select
              value={expiryHours}
              onChange={e => setExpiryHours(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value={24}>24 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>1 week</option>
              <option value={720}>30 days</option>
            </select>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              onClick={handleShare}
              disabled={sharing || !email}
              className="btn-primary flex-1 flex items-center justify-center"
            >
              {sharing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const DriverPassportPage = () => {
  const { user } = useExchangeAuth();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedWith, setSharedWith] = useState([]);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const userId = user?._id || user?.id;
  
  // Fetch or create passport
  const fetchPassport = async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/xchange/compliance/passport/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setPassport(data);
      } else if (response.status === 404) {
        // No passport yet
        setPassport(null);
      }
    } catch (error) {
      console.error('Failed to fetch passport:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const generatePassport = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/xchange/compliance/passport/create?driver_id=${userId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPassport(data.passport);
          toast.success('Credential Passport generated!');
        }
      } else {
        toast.error('Failed to generate passport');
      }
    } catch (error) {
      console.error('Failed to generate passport:', error);
      toast.error('Failed to generate passport');
    } finally {
      setGenerating(false);
    }
  };
  
  const refreshPassport = async () => {
    setLoading(true);
    await generatePassport();
    setLoading(false);
  };
  
  useEffect(() => {
    fetchPassport();
  }, [userId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }
  
  // No passport yet - show creation screen
  if (!passport) {
    return (
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-neon-cyan/20 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-neon-cyan" />
          </div>
          
          <h1 className="text-2xl font-heading font-bold text-white mb-4">
            Create Your Credential Passport
          </h1>
          
          <p className="text-slate-400 mb-8 max-w-md mx-auto">
            Your Credential Passport is a portable, verified record of all your credentials. 
            Share it instantly with any provider or broker - one verification, trusted everywhere.
          </p>
          
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { icon: QrCode, label: 'QR Code', desc: 'Instant verification' },
              { icon: Shield, label: 'Blockchain', desc: 'Tamper-proof' },
              { icon: Share2, label: 'Portable', desc: 'Share anywhere' }
            ].map((item, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg">
                <item.icon className="w-6 h-6 text-neon-cyan mx-auto mb-2" />
                <div className="text-white text-sm font-medium">{item.label}</div>
                <div className="text-xs text-slate-400">{item.desc}</div>
              </div>
            ))}
          </div>
          
          <button
            onClick={generatePassport}
            disabled={generating}
            className="btn-primary px-8 py-3"
          >
            {generating ? (
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Sparkles className="w-5 h-5 mr-2" />
            )}
            Generate My Passport
          </button>
        </motion.div>
      </div>
    );
  }
  
  const verificationUrl = passport.public_verification_url || `https://medexova.com/verify/${passport.passport_id}`;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">My Credential Passport</h1>
          <p className="text-slate-400 text-sm mt-1">Portable, verified credentials</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={refreshPassport} className="btn-secondary flex items-center">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button onClick={() => setShowShareModal(true)} className="btn-primary flex items-center">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </button>
        </div>
      </div>
      
      {/* Main passport card */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* QR Code & Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 text-center"
        >
          {/* Passport ID badge */}
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 mb-4">
            <Shield className="w-4 h-4 text-neon-cyan" />
            <span className="text-neon-cyan font-mono text-sm">{passport.passport_id}</span>
          </div>
          
          {/* QR Code */}
          <div className="bg-white p-4 rounded-xl inline-block mb-4">
            <QRCodeSVG
              value={verificationUrl}
              size={180}
              level="H"
              includeMargin={false}
              imageSettings={{
                src: "/logo-icon.png",
                height: 30,
                width: 30,
                excavate: true
              }}
            />
          </div>
          
          <p className="text-slate-400 text-sm mb-4">
            Scan to instantly verify credentials
          </p>
          
          {/* Compliance Score */}
          <div className="pt-4 border-t border-white/10">
            <div className="text-slate-400 text-sm mb-2">Compliance Score</div>
            <div className="flex justify-center">
              <ScoreCircle score={passport.compliance_score || 85} size="md" />
            </div>
          </div>
        </motion.div>
        
        {/* Verification Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 glass-card p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <FileCheck className="w-5 h-5 mr-2 text-neon-green" />
            Verification Status
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <VerificationBadge
              verified={passport.background_verified}
              label="Background Check"
              verifiedAt={passport.background_verified_at}
            />
            <VerificationBadge
              verified={passport.drug_screen_verified}
              label="Drug Screening"
              verifiedAt={passport.drug_screen_verified_at}
            />
            <VerificationBadge
              verified={passport.dmv_verified}
              label="DMV/MVR Record"
              verifiedAt={passport.dmv_verified_at}
            />
            <VerificationBadge
              verified={passport.credentials?.length > 0}
              label={`${passport.credentials?.length || 0} Credentials`}
              verifiedAt={passport.updated_at}
            />
          </div>
          
          {/* Dispatch Status */}
          <div className={`p-4 rounded-lg ${
            passport.overall_status === 'compliant' 
              ? 'bg-neon-green/10 border border-neon-green/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {passport.overall_status === 'compliant' ? (
                  <Unlock className="w-6 h-6 text-neon-green" />
                ) : (
                  <Lock className="w-6 h-6 text-red-500" />
                )}
                <div>
                  <div className={`font-semibold ${
                    passport.overall_status === 'compliant' ? 'text-neon-green' : 'text-red-500'
                  }`}>
                    {passport.overall_status === 'compliant' ? 'DISPATCH ELIGIBLE' : 'DISPATCH BLOCKED'}
                  </div>
                  <div className="text-sm text-slate-400">
                    {passport.overall_status === 'compliant' 
                      ? 'All verifications passed' 
                      : 'Pending verifications required'}
                  </div>
                </div>
              </div>
              <Activity className={`w-8 h-8 ${
                passport.overall_status === 'compliant' ? 'text-neon-green' : 'text-red-500'
              }`} />
            </div>
          </div>
          
          {/* Blockchain Hash */}
          <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-400 mb-1">Blockchain Verification Hash</div>
                <div className="font-mono text-xs text-neon-cyan truncate max-w-xs">
                  {passport.passport_hash}
                </div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(passport.passport_hash);
                  toast.success('Hash copied!');
                }}
                className="text-slate-400 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Credentials list */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-6"
      >
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <FileCheck className="w-5 h-5 mr-2 text-neon-cyan" />
          Included Credentials
        </h3>
        
        {passport.credentials && passport.credentials.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {passport.credentials.map((cred, idx) => (
              <div key={idx} className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    cred.verified ? 'bg-neon-green/20' : 'bg-slate-700'
                  }`}>
                    {cred.verified ? (
                      <CheckCircle2 className="w-4 h-4 text-neon-green" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <div className="text-white text-sm">{cred.type}</div>
                    {cred.expires && (
                      <div className="text-xs text-slate-400">
                        Expires: {new Date(cred.expires).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <FileCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No credentials included yet</p>
            <p className="text-sm">Upload credentials to add them to your passport</p>
          </div>
        )}
      </motion.div>
      
      {/* Shared with organizations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="glass-card p-6"
      >
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-neon-purple" />
          Shared With
        </h3>
        
        {passport.shared_with && passport.shared_with.length > 0 ? (
          <div className="space-y-2">
            {passport.shared_with.map((orgId, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-5 h-5 text-slate-400" />
                  <span className="text-white">Organization #{orgId.slice(-6)}</span>
                </div>
                <span className="text-xs text-neon-green">Active</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-400">
            <Share2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Not shared with any organizations yet</p>
            <button
              onClick={() => setShowShareModal(true)}
              className="mt-3 text-neon-cyan hover:text-neon-cyan/80 text-sm"
            >
              Share your passport →
            </button>
          </div>
        )}
      </motion.div>
      
      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            passportId={passport.passport_id}
            verificationUrl={verificationUrl}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DriverPassportPage;
